import { useEffect, useState } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'

/** Real hero stats from index.html (data-count-to="100"/"48"/"200" — price lowered from 250 to 200 per the user's request). */
const STATS = [
  { value: 100, suffix: '%', key: 'stat-clients' as const },
  { value: 48, prefix: '~', suffix: 'h', key: 'stat-time' as const },
  { value: 200, suffix: ' EUR', key: 'stat-price' as const },
]

function useCountUp(target: number, reducedMotion: boolean, durationMs = 1400) {
  const [value, setValue] = useState(reducedMotion ? target : 0)

  useEffect(() => {
    if (reducedMotion) {
      setValue(target)
      return
    }
    const start = performance.now()
    let raf: number
    const tick = (now: number) => {
      const progress = Math.min((now - start) / durationMs, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(target * eased))
      if (progress < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, reducedMotion, durationMs])

  return value
}

function StatItem({
  value,
  prefix = '',
  suffix = '',
  label,
  reducedMotion,
}: {
  value: number
  prefix?: string
  suffix?: string
  label: string
  reducedMotion: boolean
}) {
  const count = useCountUp(value, reducedMotion)
  return (
    <div className="flex flex-col items-center text-center lg:items-end lg:text-right">
      <div className="font-serif text-2xl font-semibold text-accent-fire md:text-3xl">
        {prefix}
        {count}
        {suffix}
      </div>
      <div className="mt-1 max-w-[9.5rem] text-xs text-foreground-muted">{label}</div>
    </div>
  )
}

/**
 * Semantic hero content — real BS/EN copy, real stats (see translations.ts
 * header comment for provenance). Renders independently of the WebGL
 * canvas so LCP never waits on Three.js (§4.7/§11), and is the only place
 * a screen reader sees content in the hero (canvas is decorative).
 *
 * Layout note (this session's second restructure, per the user's request):
 *  - WhatsApp/Viber removed from the hero entirely, both breakpoints — a
 *    site-wide floating Viber bubble (see `ViberBubble.tsx`, mounted in
 *    `App.tsx`) now covers the "quick chat" use case on mobile, and the
 *    nav's `/kontakt` CTA covers it everywhere else. `ContactButtons` is
 *    no longer imported here at all.
 *  - The headline is no longer vertically centered mid-hero (which put it
 *    on top of the blob) — it's pinned near the TOP of the section instead
 *    (`justify-start` layout, h1 first), so it reads as sitting ABOVE the
 *    blob rather than overlapping it. It plays a one-shot fade/slide-up
 *    entrance (`.hero-intro-in`, see index.css) on first paint, gated by
 *    `reducedMotion` like the existing text-sweep.
 *  - A flexible spacer (`flex-1`) fills the middle so the blob (rendered
 *    behind this content, absolute-positioned across the whole section)
 *    has room to sit unobstructed between the headline and the content
 *    below it.
 *  - Stats: desktop keeps the `lg:` right-edge vertical column from the
 *    previous session. On mobile they've moved from "centered under the
 *    CTAs" (which sat mid-blob) to just above the very bottom of the hero,
 *    i.e. below where the blob visually sits, thanks to the spacer above.
 *  - Scroll cue: on mobile it stays in-flow, right after the stats. On
 *    desktop it's now absolutely pinned near the bottom of the section
 *    (`lg:absolute lg:bottom-8`) instead of pushed down with margin — so it
 *    can sit clear of the blob without adding any extra height to the hero
 *    section itself, per the user's explicit "don't expand the hero"
 *    constraint.
 */
export function HeroContent({ reducedMotion }: { reducedMotion: boolean }) {
  const { t } = useLanguage()

  return (
    <div className="relative z-10 flex min-h-screen w-full flex-col px-6 pt-28 lg:pt-24">
      {/* Headline — pinned near the top, above the blob. */}
      <div className="flex justify-center pt-2 lg:pt-6">
        <h1
          className={`max-w-3xl text-center font-serif text-4xl font-semibold leading-tight text-balance md:text-6xl ${
            reducedMotion ? '' : 'hero-text-sweep hero-intro-in'
          }`}
        >
          {t('hero-h1-pre')}{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-fire to-accent-amber-light">
            {t('hero-h1-highlight')}
          </span>{' '}
          {t('hero-h1-post')}
        </h1>
      </div>

      {/* Blob occupies this space — intentionally empty, the canvas is
          absolute-positioned behind the whole section (see index.tsx). */}
      <div className="flex-1" aria-hidden="true" />

      {/* Mobile: stats below the blob. Fixed 2-column grid (third stat centered
          across both columns) so every phone gets the same 2 + 1 layout. It used
          to be flex-wrap with a 10rem label cap, which only fit two per row on
          viewports >= ~412px (S24 FE) and stacked all three on ~360px (S21). */}
      <div className="mx-auto mb-6 grid w-full max-w-sm grid-cols-2 gap-x-6 gap-y-6 lg:hidden">
        {STATS.map((stat, index) => (
          <div
            key={stat.key}
            className={index === STATS.length - 1 ? 'col-span-2 flex justify-center' : 'flex justify-center'}
          >
            <StatItem
              value={stat.value}
              prefix={stat.prefix}
              suffix={stat.suffix}
              label={t(stat.key)}
              reducedMotion={reducedMotion}
            />
          </div>
        ))}
      </div>

      {/* Scroll cue — in-flow on mobile (right after the stats), absolutely
          pinned near the bottom on desktop so it never adds section height.
          Desktop price stat now sits directly above it (moved from the
          right-edge column per the user's request), stacked together so
          the group as a whole stays pinned near the bottom. */}
      <div className="mb-8 flex flex-col items-center gap-3 lg:absolute lg:inset-x-0 lg:bottom-8 lg:mb-0 lg:gap-4">
        <div className="hidden flex-col items-center text-center lg:flex">
          <div className="font-serif text-2xl font-semibold text-accent-fire md:text-3xl">200€</div>
          <div className="mt-1 text-xs text-foreground-muted">{t('hero-price-badge')}</div>
        </div>
        <a
          href="#usluge"
          className="flex flex-col items-center gap-2 text-xs text-foreground-subtle transition-colors hover:text-accent-fire"
        >
          <span className="uppercase tracking-widest">{t('hero-scroll')}</span>
          <span className={reducedMotion ? '' : 'animate-bounce'} aria-hidden="true">
            ↓
          </span>
        </a>
      </div>
    </div>
  )
}
